/** Shared ECharts styling for Leadership Console revamp */

export const CHART_PALETTE = [
  '#3B82F6',
  '#10B981',
  '#F59E0B',
  '#8B5CF6',
  '#06B6D4',
  '#F97316',
  '#EF4444',
  '#84CC16',
];

export const PARTNER_SOFT_BLUE_COLORS = [
  '#7DD3FC',
  '#67E8F9',
  '#93C5FD',
  '#BAE6FD',
  '#A5F3FC',
  '#60A5FA',
  '#38BDF8',
  '#22D3EE',
  '#CBD5E1',
  '#94A3B8',
];

export const PARTNER_BLUE_COLORS = PARTNER_SOFT_BLUE_COLORS;
export const PARTNER_CHART_PALETTE = PARTNER_SOFT_BLUE_COLORS;

export const INTERNAL_SE_SOFT_BLUE_COLORS = [
  '#7DD3FC',
  '#67E8F9',
  '#93C5FD',
  '#BAE6FD',
  '#A5F3FC',
  '#60A5FA',
  '#38BDF8',
  '#22D3EE',
  '#CBD5E1',
  '#94A3B8',
];

export const REGION_CHART_COLORS = {
  APJ: '#8B5CF6',
  EMEA: '#3B82F6',
  NA: '#06B6D4',
  LATAM: '#10B981',
};

export const NARRATIVE_COLOR_MAP = {
  'Security Incident Handling': '#3B82F6',
  'Apex-Banking': '#10B981',
  'Service Predictions': '#F59E0B',
  'Apex-Online': '#8B5CF6',
  'AIOps & Observability Narrative': '#06B6D4',
  'The Complete Banking Story': '#F97316',
  'HelixGPT - Vulnerability Resolver': '#EF4444',
  'HeliGPT - Vulnerability Resolver': '#EF4444',
  'Agentic AI Driven HRSM': '#84CC16',
  'Apex-Mobile': '#F97316',
  'Platform Narratives': '#8B5CF6',
  'ServiceOps Narrative': '#3B82F6',
  'One BMC Narrative - Mainframe & Distributed': '#10B981',
  'End-to-End Observability Narrative with Live App': '#06B6D4',
  'BMC Helix Platform Demo for Executives (CXO Dashboard Narrative)': '#8B5CF6',
  'Customer Service Management - CSM': '#F59E0B',
  'Change Management DevOps meets ITIL': '#F97316',
  'Policy Based Clustering (Network Alerts)': '#84CC16',
};

export const PARTNER_NARRATIVE_COLOR_MAP = {
  'Security Incident Handling': '#7DD3FC',
  'Apex-Banking': '#67E8F9',
  'Service Predictions': '#93C5FD',
  'Apex-Online': '#BAE6FD',
  'AIOps & Observability Narrative': '#A5F3FC',
  'The Complete Banking Story': '#60A5FA',
  'HelixGPT - Vulnerability Resolver': '#38BDF8',
  'HeliGPT - Vulnerability Resolver': '#38BDF8',
  'Agentic AI Driven HRSM': '#22D3EE',
  'Apex-Mobile': '#CBD5E1',
  'Platform Narratives': '#BAE6FD',
  'ServiceOps Narrative': '#7DD3FC',
  'One BMC Narrative - Mainframe & Distributed': '#67E8F9',
  'End-to-End Observability Narrative with Live App': '#60A5FA',
  'BMC Helix Platform Demo for Executives (CXO Dashboard Narrative)': '#A5F3FC',
  'Customer Service Management - CSM': '#93C5FD',
  'Change Management DevOps meets ITIL': '#22D3EE',
  'Policy Based Clustering (Network Alerts)': '#94A3B8',
};

export const INGESTION_COLORS = ['#3B82F6', '#F59E0B', '#10B981'];

export const PET_SHARED_COLORS = {
  pet: '#3B82F6',
  shared: '#F59E0B',
};

export const getNarrativeColor = (name, index = 0) =>
  NARRATIVE_COLOR_MAP[name] || CHART_PALETTE[index % CHART_PALETTE.length];

export const getPartnerNarrativeColor = (name, index = 0) =>
  PARTNER_NARRATIVE_COLOR_MAP[name] || PARTNER_CHART_PALETTE[index % PARTNER_CHART_PALETTE.length];

export const getInternalSEColor = (index = 0) =>
  INTERNAL_SE_SOFT_BLUE_COLORS[index % INTERNAL_SE_SOFT_BLUE_COLORS.length];

export const getRegionColor = (region) =>
  REGION_CHART_COLORS[region] || CHART_PALETTE[0];

export const axisLabel = {
  fontSize: 12,
  color: '#374151',
  fontFamily: 'Poppins, Arial, sans-serif',
};

export const axisNameStyle = {
  fontSize: 12,
  color: '#6B7280',
  fontFamily: 'Poppins, Arial, sans-serif',
};

export const splitLine = {
  lineStyle: { type: 'dashed', color: '#E5E7EB' },
};

export const tooltipBase = {
  backgroundColor: '#fff',
  borderColor: '#E5E7EB',
  borderWidth: 1,
  padding: [10, 14],
  textStyle: { color: '#111827', fontSize: 12, fontFamily: 'Poppins, Arial, sans-serif' },
};

export const legendBase = {
  textStyle: { fontSize: 12, color: '#374151', fontFamily: 'Poppins, Arial, sans-serif' },
  itemWidth: 12,
  itemHeight: 12,
  itemGap: 14,
};

export const gridStandard = { left: 52, right: 24, top: 28, bottom: 56 };
export const gridWithLegend = { left: 52, right: 24, top: 28, bottom: 72 };
export const gridHorizontalBar = { left: 120, right: 32, top: 16, bottom: 48 };

const MIN_CHART_FONT = 11;

export const compactFontSize = (size, compact) => {
  if (!compact) return size;
  if (size >= 28) return Math.max(MIN_CHART_FONT, size - 3);
  if (size >= 16) return Math.max(MIN_CHART_FONT, size - 2);
  return Math.max(MIN_CHART_FONT, size - 1);
};

export const compactGrid = (grid, compact) => {
  if (!compact) return grid;
  return Object.fromEntries(
    Object.entries(grid).map(([key, value]) => [
      key,
      typeof value === 'number' ? Math.round(value * 0.88) : value,
    ])
  );
};

export const compactChartHeight = (height, compact) => (
  compact ? Math.round(height * 0.94) : height
);

export const buildChartTheme = (compact = false) => ({
  axisLabel: {
    ...axisLabel,
    fontSize: compactFontSize(12, compact),
  },
  axisNameStyle: {
    ...axisNameStyle,
    fontSize: compactFontSize(12, compact),
  },
  tooltipBase: {
    ...tooltipBase,
    padding: compact ? [8, 12] : tooltipBase.padding,
    textStyle: {
      ...tooltipBase.textStyle,
      fontSize: compactFontSize(12, compact),
    },
  },
  legendBase: {
    ...legendBase,
    textStyle: {
      ...legendBase.textStyle,
      fontSize: compactFontSize(12, compact),
    },
    itemWidth: compact ? 11 : legendBase.itemWidth,
    itemHeight: compact ? 11 : legendBase.itemHeight,
    itemGap: compact ? 12 : legendBase.itemGap,
  },
  gridStandard: compactGrid(gridStandard, compact),
  gridWithLegend: compactGrid(gridWithLegend, compact),
  gridHorizontalBar: compactGrid(gridHorizontalBar, compact),
  fs: (size) => compactFontSize(size, compact),
});

export const shortenNarrativeLabel = (name) => {
  const map = {
    'AIOps & Observability Narrative': 'AIOps & Observability',
    'HelixGPT - Vulnerability Resolver': 'HelixGPT - Vuln Resolver',
    'The Complete Banking Story': 'Complete Banking',
    'ServiceOps Narrative': 'ServiceOps',
    'End-to-End Observability Narrative with Live App': 'End-to-End Observability',
    'Security Incident Handling': 'Security Incident',
    'One BMC Narrative - Mainframe & Distributed': 'One BMC Narrative',
  };
  return map[name] || name;
};

export const truncateChartLabel = (label, maxChars = 18) => {
  if (!label) return '';
  if (label.length <= maxChars) return label;
  return `${label.slice(0, maxChars).trim()}...`;
};

export const subtleHorizontalSplitLine = {
  show: true,
  lineStyle: { type: 'dashed', color: '#E8EEF5', width: 1 },
};
